import { useRef } from 'react'
import PropTypes from 'prop-types'
import classNames from 'classnames'

import { ListBox } from 'primereact/listbox'

import OverlayPanel from '@aytm/app/stats/components/overlay-panel/OverlayPanel'
import { pressable } from '@aytm/app/stats/components/lib/pressable'

import style from '@aytm/app/stats/page/question-views/cq/components/partial/styles/ChartModes.module.scss'

// Grouping toggles + value-axis picker for Correlation Finder. Previously markup in the
// question template wired up with jQuery (`.dlq-chart-modes`); the axis picker was a
// umSelect and is now a PrimeReact Dropdown, matching the other migrated controls.
export const GROUP_TYPES = ['interlaced', 'grouped']

export const AXIS_TYPE_OPTIONS = [
  { text: 'N', value: 'respondents' },
  { text: '%', value: 'percents' }
]

// The two glyphs are the originals from the question template, unchanged apart from the
// opacity class moving to the stylesheet.
const GROUP_TYPE_GLYPH = {
  interlaced: (
    <svg xmlns="http://www.w3.org/2000/svg" viewBox="18 10 66 90">
      <title>Interlaced chart</title>
      <g>
        <polygon
          className={style.dim}
          points="70.57 47.91 70.57 35.17 61.83 35.17 61.83 74.39 66.2 74.39 66.2 47.91 70.57 47.91"
        />
        <polygon
          className={style.dim}
          points="52.19 39.35 52.19 25.5 43.44 25.5 43.44 74.39 47.81 74.39 47.81 39.35 52.19 39.35"
        />
        <polygon
          className={style.dim}
          points="33.8 62.02 33.8 54.1 25.05 54.1 25.05 74.39 29.43 74.39 29.43 62.02 33.8 62.02"
        />
        <rect x="66.2" y="47.91" width="8.75" height="26.48" />
        <rect x="47.81" y="39.35" width="8.75" height="35.04" />
        <rect x="29.43" y="62.02" width="8.75" height="12.38" />
      </g>
    </svg>
  ),
  grouped: (
    <svg xmlns="http://www.w3.org/2000/svg" viewBox="2 10 98 90">
      <title>Grouped chart</title>
      <g>
        <rect className={style.dim} x="35.99" y="35.17" width="8.75" height="39.22" />
        <rect className={style.dim} x="21.97" y="25.5" width="8.75" height="48.89" />
        <rect className={style.dim} x="7.96" y="54.1" width="8.75" height="20.3" />
        <rect x="83.3" y="47.91" width="8.75" height="26.48" />
        <rect x="69.28" y="39.35" width="8.75" height="35.04" />
        <rect x="55.27" y="62.02" width="8.75" height="12.38" />
      </g>
    </svg>
  )
}

// The glyphs carry an svg title, which not every screen reader reads as the name of a button.
const GROUP_TYPE_NAME = {
  interlaced: 'Interlaced chart',
  grouped: 'Grouped chart'
}

const ChartModes = ({ groupType, axisType, axisTypeDisabled, onGroupTypeChange, onAxisTypeChange }) => {
  const axisRef = useRef(null)

  const onAxisSelect = event => {
    axisRef.current?.hide()
    if (event.value != null && event.value !== axisType) onAxisTypeChange?.(event.value)
  }

  const axisLabel = AXIS_TYPE_OPTIONS.find(option => option.value === axisType)?.text

  return (
    <div className={style.component}>
      {GROUP_TYPES.map(type => (
        <span
          key={type}
          data-testid={`stat-dl-cq-group-type-${type}`}
          data-active={type === groupType}
          className={classNames(style.type, { [style.active]: type === groupType })}
          aria-label={GROUP_TYPE_NAME[type]}
          aria-pressed={type === groupType}
          {...pressable(event => {
            event.preventDefault()
            if (type !== groupType) onGroupTypeChange?.(type)
          })}
        >
          {GROUP_TYPE_GLYPH[type]}
        </span>
      ))}
      {!axisTypeDisabled && (
        <div className={style.axis}>
          <OverlayPanel
            ref={axisRef}
            button={
              <span className={style['axis-button']} data-testid="stat-dl-cq-axis-button">
                {axisLabel}
              </span>
            }
          >
            <ListBox
              data-testid="stat-dl-cq-axis-options"
              value={axisType}
              options={AXIS_TYPE_OPTIONS}
              optionLabel="text"
              optionValue="value"
              onChange={onAxisSelect}
            />
          </OverlayPanel>
        </div>
      )}
    </div>
  )
}

ChartModes.propTypes = {
  groupType: PropTypes.oneOf(GROUP_TYPES).isRequired,
  axisType: PropTypes.oneOf(['respondents', 'percents']).isRequired,
  axisTypeDisabled: PropTypes.bool,
  onGroupTypeChange: PropTypes.func,
  onAxisTypeChange: PropTypes.func
}

ChartModes.displayName = 'CorrelationFinderChartModes'

export default ChartModes
